import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Truck, MapPin, User, Sparkles, ReceiptPoundSterling, BadgeCheck, ShieldCheck, CheckCircle2, Clock, Mail } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import OrderStageTimeline from '@/components/OrderStageTimeline';
import OrderStageActionModal from '@/components/OrderStageActionModal';
import OrderDeleteButton from '@/components/OrderDeleteButton';
import { formatApiError } from '@/utils/apiError';
import { InvoiceExpiredNotice } from '@/components/orders/InvoiceExpiredNotice';
import { buildGuidedItems, buildManualItems } from '@/components/proepoxy/highbuild-flow/guidedItems';

const PRIMARY = '#7A0B7A';
const SECONDARY = '#5a085a';

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

const CATEGORY_LABEL = {
  floor_prep: 'Floor Preparation',
  primer: 'Primer',
  design_coat: 'Design Coat',
  high_build: 'High Build / Flakes',
  top_coat: 'Top Coat / Sealer',
  pigment: 'Pigments',
};

export default function ProEpoxyOrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageAction, setStageAction] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [expectedEta, setExpectedEta] = useState('');
  const [submittingTransition, setSubmittingTransition] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendToInstaller, setResendToInstaller] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return fetch(`${API_URL}/api/proepoxy-orders/${orderId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(setOrder)
      .finally(() => setLoading(false));
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading order…</div>;
  if (!order) return <div className="min-h-screen flex items-center justify-center text-gray-500">Order not found.</div>;

  const status = order.status || 'submitted';
  const closeStageModal = () => {
    if (submittingTransition) return;
    setStageAction(null);
    setInvoiceNumber('');
    setPaymentMethod('');
    setExpectedEta('');
  };

  const submitTransition = async (targetStatus, extras = {}) => {
    setSubmittingTransition(true);
    try {
      const res = await fetch(`${API_URL}/api/proepoxy-orders/${orderId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: targetStatus, ...extras }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Update failed');
      }
      toast.success(`Order marked as ${targetStatus.replace('_', ' ')}`);
      closeStageModal();
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmittingTransition(false);
    }
  };

  const stageActionButtons = (() => {
    const actions = {};
    if (isStaff && status === 'submitted') {
      actions.raise_invoice = (
        <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-800 whitespace-nowrap" data-testid="proepoxy-raise-invoice-btn" onClick={() => navigate(`/staff/invoices?from=proepoxy&order=${order.id}`)}>
          <FileText className="w-3.5 h-3.5 mr-1" /> Raise invoice in Xero
        </Button>
      );
      actions.invoiced = (
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap" data-testid="proepoxy-mark-invoiced-btn" onClick={() => setStageAction('invoiced')}>
          <ReceiptPoundSterling className="w-3.5 h-3.5 mr-1" /> Mark as invoiced
        </Button>
      );
    }
    if (!isStaff && status === 'invoiced') {
      actions.paid = (
        <Button size="sm" className={order.confirmed_total != null ? "bg-teal-600 hover:bg-teal-700 text-white whitespace-nowrap" : "bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 whitespace-nowrap"} data-testid="proepoxy-mark-paid-btn" onClick={() => setStageAction('paid')}>
          {order.confirmed_total != null ? <><BadgeCheck className="w-3.5 h-3.5 mr-1" /> I've paid — mark as paid</> : <><Clock className="w-3.5 h-3.5 mr-1" /> Awaiting confirmed amount</>}
        </Button>
      );
    }
    if (isStaff && status === 'paid') {
      actions.payment_confirmed = (
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap" data-testid="proepoxy-mark-payment-confirmed-btn" onClick={() => setStageAction('payment_confirmed')}>
          <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Mark as payment confirmed
        </Button>
      );
    }
    if (isStaff && (status === 'payment_confirmed' || status === 'paid')) {
      actions.dispatched = (
        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap" data-testid="proepoxy-mark-dispatched-btn" onClick={() => setStageAction('dispatched')}>
          <Truck className="w-3.5 h-3.5 mr-1" /> Mark as dispatched
        </Button>
      );
    }
    if (!isStaff && status === 'dispatched') {
      actions.delivered = (
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap" data-testid="proepoxy-mark-delivered-btn" onClick={() => setStageAction('delivered')}>
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark as delivered
        </Button>
      );
    }
    return actions;
  })();

  const handleConfirm = (extra = {}) => {
    if (stageAction === 'invoiced') submitTransition('invoiced', { invoice_number: invoiceNumber.trim(), ...extra });
    else if (stageAction === 'payment_confirmed') submitTransition('payment_confirmed', { payment_method: paymentMethod });
    else if (stageAction === 'dispatched') submitTransition('dispatched', expectedEta ? { expected_delivery_date: expectedEta } : {});
    else submitTransition(stageAction, extra);
  };

  const grouped = {};
  const items = order.items?.length ? order.items : order.mode === 'guided' ? buildGuidedItems(order.selections?.highBuildAreas) : order.mode === 'manual' ? buildManualItems(order.selections?.manualOrder) : [];
  const reconstructed = !order.items?.length && items.length > 0;
  const resendEmail = async () => {
    setResending(true);
    try {
      const res = await fetch(`${API_URL}/api/proepoxy-orders/${order.id}/resend-email`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: reconstructed ? items : [], to_installer: resendToInstaller }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Failed to resend');
      toast.success(`Order email re-sent (${d.lines} lines) to ${d.sent_to.join(', ')}`);
      setOrder((o) => ({ ...o, items: reconstructed ? items : o.items, email_resent_at: d.email_resent_at }));
    } catch (e) { toast.error(e.message); } finally { setResending(false); }
  };
  items.forEach((it) => {
    grouped[it.category] = grouped[it.category] || [];
    grouped[it.category].push(it);
  });

  const systemLabel = `${(order.system || '').replace('_', ' ').toUpperCase()}${order.mode ? ' · ' + order.mode : ''}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${SECONDARY})` }}>
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center gap-4">
          <button onClick={() => navigate('/proepoxy/orders')} className="p-2 hover:bg-white/10 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl md:text-2xl font-bold">{order.reference}</h1>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/20">
                {order.status}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white text-[#7A0B7A]">
                <Sparkles className="w-3 h-3" /> {systemLabel}
              </span>
            </div>
            <p className="text-white/70 text-sm">Order details</p>
          </div>
          <div className="ml-auto text-right flex items-center gap-3">
            <OrderDeleteButton order={order} product="proepoxy" backTo="/proepoxy/orders" />
            <div>
              <div className="text-xs text-white/60 uppercase tracking-wider">{order.confirmed_total != null ? 'Confirmed by Ox Resin' : 'Estimate · to be confirmed'}</div>
              <div className="text-2xl font-bold" data-testid="proepoxy-detail-total">{fmtGBP(order.confirmed_total ?? order.total)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Stage Timeline + action buttons */}
        <InvoiceExpiredNotice order={order} product="proepoxy" isStaff={isStaff} onReactivated={load} />
        <Card className="p-4" data-testid="proepoxy-stage-timeline-card">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Order Stages</h2>
          <OrderStageTimeline order={order} actions={stageActionButtons} />
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-2">
              <User className="w-3 h-3" /> Installer
            </div>
            <div className="font-semibold text-gray-900">{order.installer_name}</div>
            <div className="text-xs text-gray-600">{order.installer_email}</div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-2">
              <MapPin className="w-3 h-3" /> Delivery
            </div>
            <div className="text-sm text-gray-900">{order.company_name}</div>
            <div className="text-xs text-gray-600 whitespace-pre-line">{order.delivery_address || '—'}</div>
            {order.delivery_phone && <div className="text-xs text-gray-600">{order.delivery_phone}</div>}
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-2">
              <Truck className="w-3 h-3" /> Notes
            </div>
            <div className="text-sm"><strong>Ref:</strong> {order.customer_reference || '—'}</div>
            <div className="text-xs text-gray-600 mt-1">{order.delivery_notes || 'No delivery notes'}</div>
          </Card>
        </div>

        {reconstructed && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="pe-items-reconstructed">Product lines shown below were rebuilt from the installer's wizard selections (this order was placed before the line-item fix).{isStaff ? ' Use "Resend order email" to send them to the office and save them on the order.' : ''}</div>}
        {items.length === 0 && <Card className="p-5 text-sm text-amber-800" data-testid="pe-items-missing">This order has no product lines recorded — check with the installer what was needed.</Card>}
        {isStaff && items.length > 0 && (
          <Card className="p-4 flex flex-wrap items-center gap-3" data-testid="pe-resend-card">
            <Mail className="w-4 h-4 text-[#7A0B7A]" />
            <div className="flex-1 min-w-[200px] text-sm">
              <div className="font-semibold text-gray-900">Resend order email</div>
              <div className="text-xs text-gray-500">{order.email_resent_at ? `Last re-sent ${new Date(order.email_resent_at).toLocaleString('en-GB')} by ${order.email_resent_by || 'staff'}` : 'Sends the full order with its product lines to the Ox Resin office inbox again.'}</div>
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" checked={resendToInstaller} onChange={(e) => setResendToInstaller(e.target.checked)} data-testid="pe-resend-installer" /> Also send to installer</label>
            <Button size="sm" onClick={resendEmail} disabled={resending} className="bg-[#7A0B7A] hover:bg-[#5a085a] text-white" data-testid="pe-resend-btn">{resending ? 'Sending…' : 'Resend order email'}</Button>
          </Card>
        )}

        {Object.entries(CATEGORY_LABEL).map(([cat, label]) => {
          const items = grouped[cat];
          if (!items || items.length === 0) return null;
          return (
            <Card key={cat} className="p-5">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#7A0B7A]" /> {label}
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                    <th className="py-2 pr-2">Qty</th>
                    <th className="py-2 pr-2">Product</th>
                    <th className="py-2 pr-2 text-right">Unit</th>
                    <th className="py-2 pr-2 text-right">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={`${cat}-${it.product_id || it.product_name}-${idx}`} className="border-b last:border-0">
                      <td className="py-2 pr-2 font-semibold">{it.qty}×</td>
                      <td className="py-2 pr-2">
                        <div className="flex items-center gap-2">
                          {it.colour_hex && <span title={it.colour_name || ''} className="inline-block w-4 h-4 rounded border border-gray-400 shrink-0" style={{ background: it.colour_hex }} data-testid={`pe-swatch-${cat}-${idx}`} />}
                          <span>{it.product_name}</span>
                        </div>
                        <div className="text-xs text-gray-500">{it.pack_label}{it.area_name ? ` · ${it.area_name}` : ''}</div>
                      </td>
                      <td className="py-2 pr-2 text-right text-gray-600">{fmtGBP(it.unit_price)}</td>
                      <td className="py-2 pr-2 text-right font-semibold">{fmtGBP(it.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          );
        })}

        <Card className="p-5" style={{ backgroundColor: PRIMARY }}>
          <div className="flex items-center justify-between text-white">
            <div className="text-xs uppercase tracking-wider opacity-70">{order.confirmed_total != null ? 'Confirmed total (inc. VAT)' : 'Estimate (excl. VAT)'}</div>
            <div className="text-2xl font-bold">{fmtGBP(order.confirmed_total ?? order.total)}</div>
          </div>
          {order.confirmed_total == null && !isStaff && ['submitted', 'invoiced'].includes(order.status) && (
            <p className="mt-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="proepoxy-dont-pay-notice"><strong>Please don't pay yet.</strong> This is the app's estimate — the Ox Resin office will confirm the exact amount to pay and you'll see it here as <em>Confirmed</em>.</p>
          )}
        </Card>
      </div>

      <OrderStageActionModal
        action={stageAction}
        order={order}
        invoiceNumber={invoiceNumber}  setInvoiceNumber={setInvoiceNumber}
        paymentMethod={paymentMethod}  setPaymentMethod={setPaymentMethod}
        expectedEta={expectedEta}      setExpectedEta={setExpectedEta}
        submitting={submittingTransition}
        isStaff={isStaff}
        onClose={closeStageModal}
        onConfirm={handleConfirm}
        accentClass="bg-[#7A0B7A] hover:bg-[#5a085a]"
      />
    </div>
  );
}
